import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Zap, Crown, Star, Sparkles, Image, Search, FileText, CheckSquare, Trophy } from 'lucide-react';
import { SubscriptionPlan } from '@/types/subscription-types';
import { cn } from '@/lib/utils';
import { useDynamicFeatureConfig } from '@/config/dynamic-credit-features-config';

interface PlanCarouselCardProps {
  plan: SubscriptionPlan;
  isCurrentPlan: boolean;
  isSelectedPlan: boolean;
  onSelectPlan: (planId: string) => void;
  isPopular?: boolean;
}

const PlanCarouselCard: React.FC<PlanCarouselCardProps> = ({
  plan,
  isCurrentPlan,
  isSelectedPlan,
  onSelectPlan,
  isPopular = false
}) => {
  // Get dynamic feature calculations
  const { calculateTotalUses, featureConfigs } = useDynamicFeatureConfig();
  // Get plan icon based on plan name/price
  const getPlanIcon = () => {
    // Check for specific plan names first
    if (plan.name.toLowerCase().includes('pro') && !plan.name.toLowerCase().includes('master')) return Trophy;
    if (plan.name.toLowerCase().includes('master')) return Crown;
    
    // Fallback to price-based logic
    if (plan.price === 0) return Star;
    if (plan.price <= 10) return Zap;
    if (plan.price <= 50) return Sparkles;
    return Crown;
  };

  // Get plan color scheme
  const getColorScheme = () => {
    if (isCurrentPlan) {
      return {
        gradient: 'from-green-500/10 to-emerald-500/10',
        border: 'border-green-200 dark:border-green-700',
        icon: 'from-green-500 to-emerald-600',
        badge: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
      };
    }
    
    if (isSelectedPlan) {
      return {
        gradient: 'from-purple-500/10 to-violet-500/10',
        border: 'border-purple-300 dark:border-purple-600 ring-2 ring-purple-200 dark:ring-purple-700',
        icon: 'from-purple-500 to-violet-600',
        badge: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
      };
    }

    // Special styling for Most Popular plan
    if (isPopular) {
      return {
        gradient: 'from-amber-500/10 to-yellow-500/10',
        border: 'border-gray-200 dark:border-gray-700 hover:border-amber-300 dark:hover:border-amber-600',
        icon: 'from-amber-500 to-yellow-600',
        badge: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
      };
    }

    if (plan.price === 0) {
      return {
        gradient: 'from-gray-500/5 to-slate-500/5',
        border: 'border-gray-200 dark:border-gray-700',
        icon: 'from-gray-500 to-slate-600',
        badge: 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300'
      };
    }

    return {
      gradient: 'from-blue-500/5 to-indigo-500/5',
      border: 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600',
      icon: 'from-blue-500 to-indigo-600',
      badge: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
    };
  };

  const colorScheme = getColorScheme();
  const PlanIcon = getPlanIcon();

  // Calculate actual usage breakdown based on plan credits
  const usageBreakdown = calculateTotalUses(plan.aiTokens);
  
  // Get key usage metrics with proper formatting and icons
  const getUsageFeatures = () => {
    const features = [];
    
    // Image Generation - Blue
    const imageFeature = featureConfigs.find(f => f.operation_type === 'image_generation');
    if (imageFeature && usageBreakdown[imageFeature.id]) {
      features.push({
        name: `${usageBreakdown[imageFeature.id].toLocaleString()} Images`,
        icon: Image,
        color: 'text-blue-500 dark:text-blue-400',
        included: true
      });
    }
    
    // Research Generation - Green
    const researchFeature = featureConfigs.find(f => f.operation_type === 'research');
    if (researchFeature && usageBreakdown[researchFeature.id]) {
      features.push({
        name: `${usageBreakdown[researchFeature.id].toLocaleString()} Research Articles`,
        icon: Search,
        color: 'text-green-500 dark:text-green-400',
        included: true
      });
    }
    
    // Text Generation - Purple (Documents)
    const textFeature = featureConfigs.find(f => f.operation_type === 'text_generation');
    if (textFeature && usageBreakdown[textFeature.id]) {
      features.push({
        name: `${usageBreakdown[textFeature.id].toLocaleString()} Documents`,
        icon: FileText,
        color: 'text-purple-500 dark:text-purple-400',
        included: true
      });
    }
    
    // Task Recommendations - Amber/Orange
    const taskFeature = featureConfigs.find(f => f.operation_type === 'threaded_generation');
    if (taskFeature && usageBreakdown[taskFeature.id]) {
      features.push({
        name: `${usageBreakdown[taskFeature.id].toLocaleString()} Recommended Tasks`,
        icon: CheckSquare,
        color: 'text-amber-500 dark:text-amber-400',
        included: true
      });
    }
    
    // Return top 4 features, fallback to generic if none found
    return features.length > 0 
      ? features.slice(0, 4)
      : [
          { name: 'AI Features Access', icon: Sparkles, color: 'text-blue-500 dark:text-blue-400', included: true },
          { name: 'Cloud Storage', icon: CheckCircle, color: 'text-green-500 dark:text-green-400', included: true },
          { name: 'Priority Support', icon: CheckCircle, color: 'text-purple-500 dark:text-purple-400', included: true }
        ];
  };

  const keyFeatures = getUsageFeatures();

  const handleCardClick = () => {
    if (!isCurrentPlan) {
      onSelectPlan(plan.id);
    }
  };

  return (
    <motion.div
      onClick={handleCardClick}
      whileHover={!isCurrentPlan ? { y: -4, scale: 1.02, zIndex: 20 } : {}}
      whileTap={!isCurrentPlan ? { scale: 0.98 } : {}}
      className={cn(
        "relative h-full bg-white dark:bg-gray-900 rounded-2xl border-2 transition-all duration-300 cursor-pointer overflow-hidden origin-center",
        colorScheme.border,
        isCurrentPlan && "cursor-default",
        !isCurrentPlan && "hover:shadow-xl hover:shadow-purple-500/10 hover:z-20"
      )}
      style={{ transformOrigin: 'center center' }}
    >
      {/* Background gradient */}
      <div className={cn("absolute inset-0 bg-gradient-to-br", colorScheme.gradient)} />
      
      {/* Popular badge */}
      {isPopular && !isCurrentPlan && (
        <div className="absolute -top-0 left-1/2 transform -translate-x-1/2 translate-y-0 z-10">
          <div className="bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs font-bold py-1.5 px-4 rounded-b-lg shadow-lg">
            Most Popular
          </div>
        </div>
      )}

      {/* Current plan badge */}
      {isCurrentPlan && (
        <div className="absolute -top-0 -right-0 z-10">
          <div className={cn("py-1.5 px-4 rounded-bl-lg text-xs font-bold", colorScheme.badge)}>
            Current Plan
          </div>
        </div>
      )}

      {/* Selected plan indicator */}
      {isSelectedPlan && !isCurrentPlan && (
        <div className="absolute -top-0 -right-0 z-10">
          <div className={cn("py-1.5 px-4 rounded-bl-lg text-xs font-bold", colorScheme.badge)}>
            Selected
          </div>
        </div>
      )}

      <div className="relative p-6 h-full flex flex-col">
        {/* Header with icon and name */}
        <div className="flex items-center space-x-3 mb-4">
          <div className={cn("w-10 h-10 rounded-xl bg-gradient-to-r flex items-center justify-center shadow-lg", colorScheme.icon)}>
            <PlanIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              {plan.name.replace(/^AI /, '')}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {plan.billingPeriod === 'month' ? 'Monthly' : 'Yearly'}
            </p>
          </div>
        </div>

        {/* Price */}
        <div className="mb-4">
          <div className="flex items-baseline space-x-1">
            {/* Show slashed price for Leader plan */}
            {plan.name.toLowerCase().includes('leader') && (
              <span className="text-3xl font-bold text-red-500 dark:text-red-400 line-through mr-2">
                $99
              </span>
            )}
            <span className="text-3xl font-bold text-gray-900 dark:text-white">
              {plan.price === 0 ? 'Free' : `$${plan.price}`}
            </span>
            {plan.price > 0 && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                /month <span className="text-xs opacity-60">USD</span>
              </span>
            )}
          </div>
        </div>

        {/* Credits */}
        <div className="mb-6">
          <div className="flex items-center space-x-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
            <Zap className="w-4 h-4 text-amber-500" />
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {plan.aiTokens.toLocaleString()} Credits
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              per month
            </span>
          </div>
        </div>

        {/* Key Features */}
        <div className="flex-grow">
          <div className="space-y-3">
            {keyFeatures.map((feature, index) => {
              const FeatureIcon = feature.icon || CheckCircle;
              const iconColorClass = feature.color || 'text-blue-500 dark:text-blue-400';
              const isLastItem = index === keyFeatures.length - 1;
              
              return (
                <div key={index} className="flex items-start space-x-2">
                  <FeatureIcon className={cn("w-4 h-4 mt-0.5 flex-shrink-0", iconColorClass)} />
                  <span className="text-sm text-gray-700 dark:text-gray-300 leading-tight">
                    {feature.name}
                    {!isLastItem && (
                      <span className="text-xs text-gray-400 dark:text-gray-500 font-light italic ml-2">
                        or
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
          
          {/* Special Deal - positioned after features */}
          {plan.specialDeal && (
            <div className="mt-4 p-3 bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 rounded-lg border border-red-200 dark:border-red-700">
              <div className="text-sm font-semibold text-red-700 dark:text-red-400 text-center">
                {plan.specialDeal}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          {isCurrentPlan ? (
            <div className="text-center py-2 text-sm font-medium text-green-600 dark:text-green-400">
              ✓ Your Current Plan
            </div>
          ) : (
            <div className="text-center py-2 text-sm text-gray-600 dark:text-gray-400">
              {isSelectedPlan ? 'Selected for upgrade' : 'Click to select'}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default PlanCarouselCard;